import { Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import _ from "lodash";
import React, { useState } from "react";
import { Controller } from "react-hook-form";
import { ValidationSeverity } from "../../../interfaces/doc";
import { ExtractionQuerySchemaType } from "../../../schemas/doc_config";
import FormTextField from "../../Common/FormTextField";
import { useFormConfigContext } from "./ConfigFormContext";

interface ConfigValidationRuleProps {
  index: number;
  removeRule: Function;
  path: "document_rules" | "asset_rules";
}

function severityColor(severity: ValidationSeverity) {
  if (severity === ValidationSeverity.ERROR) {
    return "error";
  } else if (severity === ValidationSeverity.INFO) {
    return "info";
  } else if (severity === ValidationSeverity.WARNING) {
    return "warning";
  } else {
    return "default";
  }
}

const ConfigValidationRule: React.FC<ConfigValidationRuleProps> = ({
  index,
  removeRule,
  path,
}) => {
  const { control, watch } = useFormConfigContext();
  const [open, setOpen] = useState<boolean>(false);
  const rulePath = `config.validation.${path}.${index}` as const;

  const rule = watch(`${rulePath}`) as ExtractionQuerySchemaType;

  const getContent = () => {
    if ("check" in rule) {
      const rulePath = `config.validation.asset_rules.${index}` as const;
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {rule.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>LLM</Typography>
                <Button color="error" onClick={() => removeRule()}>
                  <Delete color="error" />
                </Button>
              </div>
            </div>
            <Typography fontSize={12} className="truncate">
              {rule.check as string}
            </Typography>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${rulePath}.name`}
                placeholder="Nom de la validation"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <FormTextField
              control={control}
              name={`${rulePath}.check`}
              placeholder="Validation"
              variant="standard"
              fullWidth
            />
            <FormControl>
              <FormLabel id="query_label">Choix de la severité</FormLabel>
              <Controller
                control={control}
                name={`${rulePath}.severity`}
                render={({ field }) => (
                  <Select
                    size="small"
                    className="flex flex-1"
                    labelId="query_label"
                    {...field}
                    renderValue={(severity) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          color={severityColor(severity)}
                          label={severity}
                        />
                      </Box>
                    )}
                  >
                    {_.values(ValidationSeverity).map((severity) => (
                      <MenuItem key={severity} value={severity}>
                        {severity}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <FormTextField
              control={control}
              name={`${rulePath}.success_message`}
              placeholder="Message en cas de succès"
              label="Message en cas de succès"
              variant="standard"
              fullWidth
              multiline
            />
            <FormTextField
              control={control}
              name={`${rulePath}.error_message`}
              placeholder="Message en cas d'erreur"
              label="Message en cas d'erreur"
              variant="standard"
              fullWidth
              multiline
            />
            <FormTextField
              control={control}
              name={`${rulePath}.untreatable_message`}
              placeholder="Message en cas d'impossibilité de traiter la règle"
              label="Message en cas d'impossibilité de traiter la règle"
              variant="standard"
              fullWidth
              multiline
            />
          </Stack>
        );
      }
    } else if ("expression" in rule)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {rule.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>Formule</Typography>
                <Button color="error" onClick={() => removeRule()}>
                  <Delete color="error" />
                </Button>
              </div>
            </div>
            <Typography fontSize={12} className="truncate">
              {rule.expression}
            </Typography>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${rulePath}.name`}
                placeholder="Nom de la validation"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <FormTextField
              control={control}
              name={`${rulePath}.expression`}
              placeholder="Expression JSONATA"
              variant="standard"
              fullWidth
            />
            <FormControl>
              <FormLabel id="query_label">Choix de la severité</FormLabel>
              <Controller
                control={control}
                name={`${rulePath}.severity`}
                render={({ field }) => (
                  <Select
                    size="small"
                    className="flex flex-1"
                    labelId="query_label"
                    {...field}
                  >
                    {_.values(ValidationSeverity).map((severity) => (
                      <MenuItem key={severity} value={severity}>
                        {severity}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <FormTextField
              control={control}
              name={`${rulePath}.success_message`}
              placeholder="Message en cas de succès"
              label="Message en cas de succès"
              variant="standard"
              fullWidth
              multiline
            />
            <FormTextField
              control={control}
              name={`${rulePath}.error_message`}
              placeholder="Message en cas d'erreur"
              label="Message en cas d'erreur"
              variant="standard"
              fullWidth
              multiline
            />
            <FormTextField
              control={control}
              name={`${rulePath}.untreatable_message`}
              placeholder="Message en cas d'impossibilité de traiter la règle"
              label="Message en cas d'erreur"
              variant="standard"
              fullWidth
              multiline
            />
          </Stack>
        );
      }
    else return null;
  };

  return (
    <Paper
      square={false}
      elevation={3}
      className="flex flex-col gap-2 p-2 border-[var(--mui-palette-primary-main)] border-solid	border-1 hover:cursor-pointer"
      onClick={!open ? () => setOpen(true) : undefined}
    >
      {getContent()}
    </Paper>
  );
};

export default ConfigValidationRule;
